import { useQueries, useQuery } from '@tanstack/react-query';
import type { Integration } from '@lizrd/contracts';
import { getOrgSavingsSummary, listIntegrations } from '../../lib/api';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { SkeletonText } from '../ui/skeleton';
import { InfoButton } from '../ui/info-button';

/** Roll a single domain's integrations into one connection state. */
type ConnState = 'connected' | 'attention' | 'error' | 'none';
function connState(integrations: Integration[]): ConnState {
  if (integrations.length === 0) return 'none';
  if (integrations.some((i) => i.status === 'ERROR')) return 'error';
  if (integrations.some((i) => ['WARNING', 'DISCONNECTED', 'PENDING'].includes(i.status)))
    return 'attention';
  return 'connected';
}

const CONN_META: Record<
  ConnState,
  { label: string; variant: 'success' | 'warning' | 'danger' | 'neutral' }
> = {
  connected: { label: 'connected', variant: 'success' },
  attention: { label: 'needs attention', variant: 'warning' },
  error: { label: 'error', variant: 'danger' },
  none: { label: 'no sources', variant: 'neutral' },
};

/**
 * Per-domain connection roll-up (DASH-006): whether every domain is
 * healthily connected to its clouds and repos — the sources Lizrd needs to find
 * and prove savings. Carries an "ⓘ" explaining the states.
 */
export function DomainsConnectionStatus() {
  const summary = useQuery({ queryKey: ['org-savings-summary'], queryFn: getOrgSavingsSummary });
  const envs = summary.data?.domains ?? [];
  const integrationQs = useQueries({
    queries: envs.map((e) => ({
      queryKey: ['integrations', e.domainId],
      queryFn: () => listIntegrations(e.domainId),
      refetchInterval: 30_000,
    })),
  });

  const conn = envs.map((e, i) => ({
    name: e.name,
    state: connState(integrationQs[i]?.data ?? []),
  }));
  const connectedCount = conn.filter((c) => c.state === 'connected').length;
  const connLoaded = integrationQs.every((q) => q.data !== undefined);

  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center gap-1.5">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
          Domains connection status
        </h2>
        <InfoButton label="Domains connection status">
          <p>
            Whether each domain is healthily connected to its data sources — your cloud accounts
            (AWS…) and code repositories. Lizrd can only find and prove savings for sources it can
            reach.
          </p>
          <p>
            <b className="text-bone">Connected</b> means every source is syncing normally.{' '}
            <b className="text-bone">Needs attention</b> flags a pending or degraded connection, and{' '}
            <b className="text-bone">error</b> means a source failed — open that domain&apos;s
            Settings to fix it.
          </p>
        </InfoButton>
      </div>
      {!summary.data || !connLoaded ? (
        <SkeletonText lines={3} lineClassName="h-4" />
      ) : conn.length === 0 ? (
        <p className="text-sm text-muted">No domains yet.</p>
      ) : (
        <>
          <p className="mb-3 text-sm text-haze">
            <span className="font-medium text-bone">{connectedCount}</span> of {conn.length} domains
            fully connected.
          </p>
          <ul className="space-y-2 text-sm">
            {conn.map((c) => (
              <li key={c.name} className="flex items-center justify-between gap-3">
                <span className="truncate text-haze">{c.name}</span>
                <Badge variant={CONN_META[c.state].variant} dot>
                  {CONN_META[c.state].label}
                </Badge>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
